import { useState } from "react";
import {
  TestTube,
  PaperPlaneTilt,
  CheckCircle,
  Warning,
  WhatsappLogo,
  EnvelopeSimple,
  Lightning,
  Scales,
} from "@phosphor-icons/react";
import { postSimulate, postCompare, patchCompareVote } from "@/lib/api";
import { toast } from "sonner";

const EXAMPLES = [
  { name: "Maria G.", body: "Hi! What are your opening hours today?" },
  { name: "Tom P.", body: "Do you ship internationally? How long to Germany?" },
  { name: "Sarah L.", body: "I want a refund on order #4521 — it arrived broken." },
  { name: "Kevin M.", body: "Do you serve oat milk?" },
];

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo },
  { key: "email", label: "Email", icon: EnvelopeSimple },
  { key: "simulator", label: "Test", icon: Lightning },
];

function ModelCard({ title, result }) {
  if (!result) return null;
  return (
    <div className="card-soft p-5 flex flex-col gap-3">
      <div className="overline">{title}</div>
      {result.escalated ? (
        <div className="flex items-center gap-2">
          <Warning size={18} weight="fill" className="text-destructive" />
          <span className="bg-destructive text-white px-2 py-1 text-[10px] font-medium uppercase tracking-wider">
            Eskaliert
          </span>
        </div>
      ) : (
        <div className="border border-border-soft rounded-xl p-4 bg-secondary/30 whitespace-pre-wrap leading-relaxed text-sm">
          {result.reply}
        </div>
      )}
      <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
        <span>Tokens: {result.tokens_in} in / {result.tokens_out} out</span>
        <span>Kosten: ${result.cost_usd?.toFixed(5)}</span>
      </div>
    </div>
  );
}

function JudgeSection({ compareId, judge }) {
  const [voted, setVoted] = useState(null);
  const [saving, setSaving] = useState(false);

  const vote = async (winner) => {
    setSaving(true);
    try {
      await patchCompareVote(compareId, winner);
      setVoted(winner);
    } catch {
      toast.error("Abstimmung fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const winnerLabel =
    judge.winner === "sonnet"
      ? "Sonnet"
      : judge.winner === "haiku"
      ? "Haiku"
      : "Unentschieden";

  const VOTE_BTNS = [
    { key: "sonnet", label: "Sonnet" },
    { key: "tie", label: "Unentschieden" },
    { key: "haiku", label: "Haiku" },
  ];

  return (
    <div className="card-soft p-6 mt-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-secondary/60 flex items-center justify-center">
          <Scales size={16} weight="duotone" className="text-muted-foreground" />
        </div>
        <div className="overline">Judge-Urteil</div>
      </div>
      <div className="mb-1 font-medium text-sm">
        Gewinner: <span className="font-semibold">{winnerLabel}</span>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
        {judge.reasoning}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground mr-1">Dein Urteil:</span>
        {VOTE_BTNS.map((btn) => (
          <button
            key={btn.key}
            disabled={saving || voted !== null}
            onClick={() => vote(btn.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              voted === btn.key
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border-soft hover:bg-secondary disabled:opacity-50"
            }`}
          >
            {btn.label}
          </button>
        ))}
        {voted && (
          <span className="text-xs text-accent font-medium ml-2">Gespeichert ✓</span>
        )}
      </div>
    </div>
  );
}

export default function Simulator() {
  const [mode, setMode] = useState("simulate"); // "simulate" | "compare"

  // Simulate state
  const [channel, setChannel] = useState("simulator");
  const [senderName, setSenderName] = useState("Test Customer");
  const [senderId, setSenderId] = useState("customer@example.com");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Compare state
  const [cmpBody, setCmpBody] = useState("");
  const [cmpSubject, setCmpSubject] = useState("");
  const [cmpResult, setCmpResult] = useState(null);
  const [cmpLoading, setCmpLoading] = useState(false);

  const run = async () => {
    if (!body.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await postSimulate({
        channel,
        sender_name: senderName,
        sender_id: senderId,
        subject,
        body: body.trim(),
      });
      setResult(res);
      if (res.status === "escalated") {
        toast.warning("Bot escalated this message to you.");
      } else {
        toast.success("Bot generated an auto-reply.");
      }
    } catch (e) {
      toast.error("Failed to simulate.");
    } finally {
      setLoading(false);
    }
  };

  const runCompare = async () => {
    if (!cmpBody.trim()) return;
    setCmpLoading(true);
    setCmpResult(null);
    try {
      const res = await postCompare(cmpBody.trim(), cmpSubject);
      setCmpResult(res);
      toast.success("Vergleich abgeschlossen.");
    } catch {
      toast.error("Vergleich fehlgeschlagen.");
    } finally {
      setCmpLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-5xl mx-auto" data-testid="simulator-page">
      <div className="mb-8">
        <div className="overline">Simulator</div>
        <h1 className="font-semibold text-3xl sm:text-4xl tracking-tight mt-2">
          Teste wie dein Bot antwortet
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
          Schick eine simulierte Kunden-Nachricht. Der Bot antwortet auf Basis
          deiner FAQs und Business-Daten, oder eskaliert an dich wenn er unsicher ist.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex rounded-xl overflow-hidden border border-border-soft w-fit mb-6">
        {[
          { key: "simulate", label: "Simulieren" },
          { key: "compare", label: "Vergleichen" },
        ].map((m) => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            className={`px-5 py-2 text-xs font-medium uppercase tracking-wider border-r border-border-soft last:border-r-0 transition-colors ${
              mode === m.key
                ? "bg-primary text-primary-foreground"
                : "bg-white hover:bg-secondary"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* ── SIMULIEREN ── */}
      {mode === "simulate" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Input */}
          <div className="card-soft p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-secondary/60 flex items-center justify-center">
                <TestTube size={16} weight="duotone" className="text-muted-foreground" />
              </div>
              <div className="overline">Eingehende Nachricht</div>
            </div>

            <div className="mb-4">
              <div className="overline mb-2">Kanal</div>
              <div className="flex rounded-xl overflow-hidden border border-border-soft">
                {CHANNELS.map((c) => {
                  const Icon = c.icon;
                  const active = channel === c.key;
                  return (
                    <button
                      key={c.key}
                      data-testid={`sim-channel-${c.key}`}
                      onClick={() => setChannel(c.key)}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium uppercase tracking-wider border-r border-border-soft last:border-r-0 transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground"
                          : "bg-white hover:bg-secondary"
                      }`}
                    >
                      <Icon size={14} weight="bold" /> {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="overline block mb-2">Absender-Name</label>
                <input
                  data-testid="sim-name"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  {channel === "whatsapp" ? "Telefon" : "Email / ID"}
                </label>
                <input
                  data-testid="sim-id"
                  value={senderId}
                  onChange={(e) => setSenderId(e.target.value)}
                  className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                />
              </div>
            </div>

            {channel === "email" && (
              <div className="mb-4">
                <label className="overline block mb-2">Betreff</label>
                <input
                  data-testid="sim-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Frage zu meiner Bestellung"
                  className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
                />
              </div>
            )}

            <div className="mb-4">
              <label className="overline block mb-2">Nachricht</label>
              <textarea
                data-testid="sim-body"
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Tippe hier die Kunden-Nachricht…"
                className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 resize-y transition-all"
              />
            </div>

            <div className="mb-4">
              <div className="overline mb-2">Schnellbeispiele</div>
              <div className="flex flex-wrap gap-2">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={i}
                    data-testid={`sim-example-${i}`}
                    className="border border-border-soft rounded-full px-3 py-1.5 text-xs hover:bg-secondary transition-colors"
                    onClick={() => {
                      setSenderName(ex.name);
                      setBody(ex.body);
                    }}
                  >
                    {ex.body.slice(0, 40)}…
                  </button>
                ))}
              </div>
            </div>

            <button
              className="btn-invert w-full justify-center disabled:opacity-50"
              disabled={loading || !body.trim()}
              onClick={run}
              data-testid="sim-run-btn"
            >
              <PaperPlaneTilt size={16} weight="bold" />
              {loading ? "Denke nach…" : "An Bot senden"}
            </button>
          </div>

          {/* Result */}
          <div className="card-soft p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <Lightning size={16} weight="duotone" className="text-accent" />
              </div>
              <div className="overline">Bot-Entscheidung</div>
            </div>
            {!result && (
              <div className="text-sm text-muted-foreground leading-relaxed">
                Schicke eine Simulation um zu sehen wie der Bot reagiert. Sichere
                Antworten gehen automatisch raus; unklare oder komplexe Nachrichten
                werden in die Inbox eskaliert.
              </div>
            )}
            {result && result.status === "escalated" && (
              <div data-testid="sim-result-escalated">
                <div className="flex items-center gap-2 mb-3">
                  <Warning size={20} weight="fill" className="text-destructive" />
                  <span className="bg-destructive text-white px-2 py-1 text-[10px] font-medium uppercase tracking-wider">
                    Eskaliert
                  </span>
                </div>
                <div className="font-display font-bold text-xl tracking-tight mb-2">
                  Der Bot hat das für dich markiert.
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Der Bot war sich unsicher. Die Nachricht liegt jetzt mit dem
                  Eskaliert-Badge in deiner Inbox und du bekommst eine
                  Benachrichtigung.
                </p>
              </div>
            )}
            {result && result.ai_reply && (
              <div data-testid="sim-result-answer">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle size={20} weight="fill" className="text-accent" />
                  <span className="bg-accent text-white px-2 py-1 text-[10px] font-medium uppercase tracking-wider">
                    Auto-Antwort
                  </span>
                </div>
                <div className="border border-border-soft rounded-xl p-4 bg-secondary/30 whitespace-pre-wrap leading-relaxed text-sm">
                  {result.ai_reply}
                </div>
                {result.delivery_error && (
                  <div className="mt-3 text-xs text-destructive">
                    Hinweis: {result.delivery_error} (Antwort generiert, nicht ausgeliefert)
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── VERGLEICHEN ── */}
      {mode === "compare" && (
        <div>
          {/* Input */}
          <div className="card-soft p-6 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-secondary/60 flex items-center justify-center">
                <Scales size={16} weight="duotone" className="text-muted-foreground" />
              </div>
              <div className="overline">Nachricht vergleichen</div>
            </div>

            <div className="mb-4">
              <label className="overline block mb-2">Betreff (optional)</label>
              <input
                value={cmpSubject}
                onChange={(e) => setCmpSubject(e.target.value)}
                placeholder="Frage zu meiner Bestellung"
                className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
              />
            </div>

            <div className="mb-4">
              <label className="overline block mb-2">Nachricht</label>
              <textarea
                rows={5}
                value={cmpBody}
                onChange={(e) => setCmpBody(e.target.value)}
                placeholder="Tippe hier die Kunden-Nachricht…"
                className="w-full border border-border-soft rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 resize-y transition-all"
              />
            </div>

            <div className="mb-4">
              <div className="overline mb-2">Schnellbeispiele</div>
              <div className="flex flex-wrap gap-2">
                {EXAMPLES.map((ex, i) => (
                  <button
                    key={i}
                    className="border border-border-soft rounded-full px-3 py-1.5 text-xs hover:bg-secondary transition-colors"
                    onClick={() => setCmpBody(ex.body)}
                  >
                    {ex.body.slice(0, 40)}…
                  </button>
                ))}
              </div>
            </div>

            <button
              className="btn-invert w-full justify-center disabled:opacity-50"
              disabled={cmpLoading || !cmpBody.trim()}
              onClick={runCompare}
            >
              <Scales size={16} weight="bold" />
              {cmpLoading ? "Vergleiche…" : "Sonnet vs. Haiku"}
            </button>
          </div>

          {/* Results */}
          {cmpLoading && (
            <div className="text-sm text-muted-foreground py-4 text-center">
              Beide Modelle antworten…
            </div>
          )}

          {cmpResult && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ModelCard title="Sonnet" result={cmpResult.sonnet} />
                <ModelCard title="Haiku" result={cmpResult.haiku} />
              </div>
              {cmpResult.judge && (
                <JudgeSection
                  compareId={cmpResult.id}
                  judge={cmpResult.judge}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
